import React from "react";
import { CashBalanceIcon } from "../../assets/icons/cashBalanceIcon";
import { CustomerIcon, TransactionIcon } from "../../assets/icons";
import { TrendStatsCard } from "./TrendStatsCard";
import { useTranslation } from "react-i18next";
import { Calendar } from "../../assets/icons/calendar";
import { DashboardStats } from "../../@types";
import BudgetHistory from "./BudgetHistory/BudgetHistory";
import { ExpenseIcon } from "../../assets/icons/expenseIcon";
import DatePickerComponent from "../datePicker/Calender";
import { NetProfitIcon } from "../../assets/icons/NetProfit";
import OverviewLoaders from "./Loading/OverviewLoaders";

interface OverviewProps {
  showCalendar: boolean;
  handleCalendarToggle: () => void;
  selectedLabel: string;
  dashboardStats: DashboardStats;
  startDate?: Date | null;
  endDate?: Date | null;
  setSelectedLabel: React.Dispatch<React.SetStateAction<string>>;
  onApplyButtonClick: (startDate: Date, endDate: Date) => Promise<void>;
  onRangeSelect: (range: [Date, Date]) => void;
  selected: [Date, Date] | null;
  isLoading: boolean;
}

const Overview = ({
  showCalendar,
  handleCalendarToggle,
  selectedLabel,
  dashboardStats,
  endDate,
  startDate,
  setSelectedLabel,
  onApplyButtonClick,
  onRangeSelect,
  selected,
  isLoading,
}: OverviewProps) => {
  const { t } = useTranslation("dashboard");
  return (
    <div className="py-3 md:py-6 bg-white mt-4 rounded-lg">
      <div className="px-3 md:px-6 flex flex-col-reverse gap-3 md:flex-row md:justify-between md:items-center w-full">
        <div>
          <h2 className="text-[#1D2939] font-semibold leading-4 text-[16px] md:text-[20px]">
            Overview
          </h2>
          <p className="text-[#475467] hidden xl:flex font-medium text-sm mt-2">
            **Figures shows the transactions for : {selectedLabel}.
          </p>
        </div>

        <DatePickerComponent
          handleCalendarToggle={handleCalendarToggle}
          selectedLabel={selectedLabel}
          showCalendar={showCalendar}
          startDate={startDate}
          endDate={endDate}
          setSelectedLabel={setSelectedLabel}
          onRangeSelect={onRangeSelect}
          selected={selected}
          onApplyButtonClick={onApplyButtonClick}
        />
      </div>
      <hr className="mt-3 mb-6 md:my-6 border border-gray-100" />
      {isLoading ? (
        <OverviewLoaders />
      ) : (
        <div className="px-3 md:px-6 grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-2  gap-4 mt-3 md:mt-4">
          {/* Cash balance */}
          <div className="lg:block w-full">
            <TrendStatsCard
              title={t("cashBalance")}
              titleIcon={<CashBalanceIcon />}
              isUptrend={dashboardStats?.cashBalance.percentageChange > 0}
              toolTipText={t("CashbalanceTooltip")}
              value={dashboardStats?.cashBalance?.value}
              isCashBalance={true}
              showArrow={true}
              percentageValue={dashboardStats?.cashBalance.percentageChange}
            />
          </div>
          <div className="hidden lg:block w-full">
            <TrendStatsCard
              title={t("netProfit")}
              titleIcon={<NetProfitIcon color="#0469C5" />}
              isUptrend={dashboardStats?.netProfit.percentageChange > 0}
              toolTipText={t("NetProfitTooltip", {
                selectedLabel: "Last 30 day",
              })}
              value={dashboardStats?.netProfit?.value}
              isCashBalance={true}
              showArrow={true}
              percentageValue={dashboardStats?.netProfit.percentageChange}
            />
          </div>
          <div className="hidden lg:block w-full">
            <TrendStatsCard
              title={"Total Expenses"}
              titleIcon={<ExpenseIcon />}
              isUptrend={dashboardStats?.transactions.percentageChange > 0}
              toolTipText={t("ExpenseTooltip", {
                selectedLabel: "Last 30 day",
              })}
              value={dashboardStats?.expenses.totalExpenseValue}
              isCashBalance={true}
            />
          </div>

          {/* Mobile */}
          <div className="flex flex-col lg:hidden w-full gap-4 md">
            <TrendStatsCard
              title={t("netProfit")}
              titleIcon={<NetProfitIcon color="#0469C5" />}
              isUptrend={dashboardStats?.netProfit.percentageChange > 0}
              toolTipText={t("NetProfitTooltip", {
                selectedLabel: "Last 30 day",
              })}
              value={dashboardStats?.netProfit?.value}
              isCashBalance={true}
              showArrow={true}
              percentageValue={dashboardStats?.netProfit.percentageChange}
            />
            <TrendStatsCard
              hide={true}
              title={"Total Expenses"}
              titleIcon={<ExpenseIcon />}
              isUptrend={dashboardStats?.transactions.percentageChange > 0}
              toolTipText={t("ExpenseTooltip", {
                selectedLabel: "Last 30 day",
              })}
              value={dashboardStats?.expenses.totalExpenseValue}
              isCashBalance={true}
            />
          </div>

          {/* BUDGET */}
          <div className="h-full w-full">
            <BudgetHistory toolTipText={t("budgetTooltip")} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
